import React, { createContext, useContext } from 'react';
import { ApiSectionOneDataRepository } from '@/features/section-1/infra/repositories/ApiSectionOneDataRepository';
import SectionOneUseCase from '../../application/use-cases/SectionOneUseCase';
import { EmployeeInfoDIProvider } from '../../features/employer-selection/infra/di/employeeInfoDIContext';
import { useHttpClientCore } from '@/shared';
import { useReduxStatesAdapter } from '../store/ReduxStateAdapter';
import { SupplementADIProvider } from '../../features/interpreter/infra/di/supplementADIContext';
import { useReduxLanguageAdaptor } from '../store/reduxLanguageAdaptor';
import { ApiAmendmentRepository } from '@/features/section-1/infra/repositories/ApiAmendmentRepository';
import { IAmendmentRepository } from '@/features/section-1/application/ports/outbound/IAmendmentRepository';
import { IRemoteI9Repository } from '@/features/remote_i9/application/ports/outbound/IRemoteI9Repository';
import { ApiPartnerPrefillRepository } from '@/features/section-1/infra/repositories/ApiPartnerPrefillRepository';
import { IPartnerPrefillRepository } from '@/features/section-1/application/ports/outbound/IPartnerPrefillRepository';
import { ApiRemoteI9Repository } from '@/features/remote_i9/infra/repositories/ApiRemoteI9Repository';

interface ISectionOneDIContext {
  sectionOneUseCase: SectionOneUseCase;
  statesAdaptor: ReturnType<typeof useReduxStatesAdapter>;
  languageAdaptor: ReturnType<typeof useReduxLanguageAdaptor>;
  amendmentRepository: IAmendmentRepository;
  remoteI9Repository: IRemoteI9Repository;
  /** Candidate prefill for a partner-originated Section 1 (VTK-1918). */
  partnerPrefillRepository: IPartnerPrefillRepository;
}

const SectionOneDIContext = createContext<ISectionOneDIContext | undefined>(undefined);

export const SectionOneDIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const httpClient = useHttpClientCore();
  const repo = new ApiSectionOneDataRepository(httpClient);
  const sectionOneUseCase = new SectionOneUseCase(repo);
  const statesAdaptor = useReduxStatesAdapter();
  const languageAdaptor = useReduxLanguageAdaptor();
  const amendmentRepository = new ApiAmendmentRepository(httpClient);
  const remoteI9Repository = new ApiRemoteI9Repository(httpClient);
  const partnerPrefillRepository = new ApiPartnerPrefillRepository(httpClient);

  return (
    <SectionOneDIContext.Provider
      value={{
        sectionOneUseCase,
        statesAdaptor,
        languageAdaptor,
        amendmentRepository,
        remoteI9Repository,
        partnerPrefillRepository,
      }}
    >
      <EmployeeInfoDIProvider>
        <SupplementADIProvider>{children}</SupplementADIProvider>
      </EmployeeInfoDIProvider>
    </SectionOneDIContext.Provider>
  );
};

export function useSectionOneDIContext() {
  const ctx = useContext(SectionOneDIContext);
  if (!ctx) throw new Error('useSectionOneDIContext must be used within SectionOneDIProvider');
  return ctx;
}
